<template>
	<div
		ref="root"
		class="entries-page"
		:data-snapped="entryDetailsSnapped"
	>
		<div class="large-scroll-container">
			<div id="c-admin" class="h-100">
				<div
					class="cognito c-admin-view c-lrg c-app-container"
					:style="{ '--entry-details-width': (isMobile ? '100vw' : `${entryDetailsPanelWidth}px`) }"
				>
					<div ref="entriesRoot" class="c-entries" :class="{'c-entries--limited-access': isLimitedAccess}">
						<div id="resizable-content">
							<FormNavigationHeader
								:global-store="globalStore"
								:is-navigating="isNavigating"
								@show-account-menu="$emit('show-account-menu', $event)"
							/>
							<CEntryViewTabsContainer
								v-if="isMobile && selectedFormName"
								:can-edit-views="canEditViewTabs"
								:show-workflow-tasks="showWorkflowTasks"
								:views="menuEntryViews"
								:current-view-entry-count="currentViewEntryCount"
								:form-data="formModel.FormData"
								:is-org-owner="isOrgOwner"
								:form-name="selectedFormName"
								:form-id="selectedFormId"
								@change-view="changeView"
								@new-view="newView"
								@reorder-view="reorderView"
								@save-view="saveView"
								@delete-view="deleteView"
							/>
							<div class="entries-page__toolbar">
								<CEntryViewTabsContainer
									v-if="!isMobile && selectedFormName"
									:can-edit-views="canEditViewTabs"
									:show-workflow-tasks="showWorkflowTasks"
									:views="menuEntryViews"
									:current-view-entry-count="currentViewEntryCount"
									:form-data="formModel.FormData"
									:is-org-owner="isOrgOwner"
									:form-name="selectedFormName"
									@save-view="saveView"
									@delete-view="deleteView"
									@change-view="changeView"
									@new-view="newView"
									@reorder-view="reorderView"
								/>
								<!-- @vue-ignore id -->
								<EntriesToolbar
									id="c-entries-legacy-content"
									ref="entriesToolbar"
									:is-limited-access="isLimitedAccess"
									:can-manage-entries="canManageEntries"
									:can-manage-form="canManageForm"
									:show-new-entry-button="showNewEntryButton"
									:new-entry-button-text="newEntryButtonText"
									:is-assigned-view="isAssignedView"
									:is-archived="isArchived"
									:is-guest="isGuest"
									:disable-unread-entry-tracking="disableUnreadEntryTracking"
									:tokens="tokens"
									:form-info="formInfo"
									:class="{ 'h-100': !isTableView }"
									class="shrink-0"
								/>
							</div>
							<EntriesFormView
								v-if="adminForm && adminForm.mount && isFormView && isFormViewReady"
								:admin-api="adminForm"
								:form-id="globalStore.currentForm?.Id ?? selectedFormId"
								:view-id="globalStore.currentView?.Id"
								:view-type="globalStore.currentView?.Type"
								:show-page-breaks="globalStore.currentView?.ShowPageBreaks"
								:is-public="isPublic"
								:role-name="workflowRoleName"
								class="border-t border-primary"
								@set-no-changes="setNoChanges"
								@form-mounted="formMounted"
								@before-submit="beforeSubmit"
								@form-view-submitted="formViewSubmitted"
								@page-loaded="pageLoaded"
							/>
						</div>
						<svg class="block" height="0px" width="0px">
							<defs>
								<filter id="signature-filter" color-interpolation-filters="sRGB">
									<feColorMatrix type="matrix" :values="colorMatrix" />
								</filter>
							</defs>
						</svg>
						<EntriesGrid
							v-show="isTableView"
							:orgcode="orgcode"
							:form-url-name="formUrlName"
							:entry-view="entryView"
							:entries-amount="globalStore.currentView?.Entries"
							class="border-t border-primary"
						/>
						<div v-show="isTableView" class="c-panel-resizer"></div>
						<!-- Mobile new entry / LA actions multi-button -->
						<button
							v-if="showMobileNewEntryButton"
							class="c-new-entry c-new-entry--mobile rounded-full absolute button--no-style p-3 bg-cta text-cta"
						>
							<Inline svg="plus" />
						</button>
						<CEntryViewActionsContainer
							v-else-if="showLAMobileActionsMenu"
							:entries-selected="hasSelectedEntries"
							:global-store="globalStore"
							:is-mobile="isMobile"
							:role-name="workflowRoleName"
							:admin-api="adminForm"
							@export="exportEntries"
							@bulk-action="bulkAction"
						/>
						<!-- @vue-ignore ref -->
						<EntryDetails
							v-if="!isGuest"
							v-show="isTableView"
							ref="entryDetails"
							:inert="!isEntryDetailsOpen"
							:orgcode="orgcode"
							:form-url-name="formUrlName"
							:view-id="currentViewId"
							:entry-number="entryNumber"
							:is-limited-access="isLimitedAccess"
							:can-manage-entries="canManageEntries"
							:can-manage-form="canManageForm"
							:is-archived="isArchived"
							:disable-unread-entry-tracking="disableUnreadEntryTracking"
							:order-export-id="orderExportId"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- Initial Sync Loading Dialog -->
		<LinkedLookupSyncDialog
			v-if="syncStatus?.syncing || syncStatus?.failed || syncStatus?.completed"
			:progress="syncStatus?.progress"
			:total-entries="syncStatus?.totalEntries"
			:completed-entries="syncStatus?.completedEntries"
			:failed="syncStatus?.failed"
			:completed="syncStatus?.completed"
			@closed="clearSyncStatus"
		/>
	</div>
</template>

<script lang="ts">
	import type { Route } from 'vue-router';
	import type { Dictionary, NavigationGuardNext } from 'vue-router/types/router';
	import type { PropType } from 'vue';
	import { defineComponent, watch, ref, computed, inject, onMounted, onUnmounted, nextTick, provide } from 'vue';
	import './library-scripts';
	import { serviceRequest } from '@cognitoforms/api/services/service-request';
	import type { RecentPage } from '@cognitoforms/types/server-types/model/recent-page';
	import { PageType } from '@cognitoforms/types/server-types/model/recent-page+-page-type';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import '@legacy/Scripts/component/jquery.fileDownload';
	import '@legacy/Scripts/component/Cognito-timepicker';
	import '@legacy/Scripts/component/Cognito-typeahead';
	import { injectDynamicPageScript, type LegacyPage } from './utils/utils';
	import { handlePageAccessError, handlePageDisposalError } from '../router';
	import { injectAdminFormApi, removeAdminFormApi } from './utils/admin-api';
	import { useAdminComponents } from './utils/admin-components';
	import { disposeLegacyPageState, ensureCognitoGlobal, resetEntityPools } from './utils/globals-management';
	import { loadRequiredScripts, loadSharedScript, loadLibraryScript, loadAdminScript } from './utils/legacy-script';
	import { appendLibraryExoWebTemplates, appendSharedExoWebTemplates } from './utils/legacy-template';
	import { buildQueryString } from '@cognitoforms/utils/url-helper';
	import EntriesGrid from './EntriesGrid.vue';
	import EntriesToolbar from './EntriesToolbar.vue';
	import EntryDetails from './EntryDetails.vue';
	import LinkedLookupSyncDialog from 'src/components/dialogs/LinkedLookupSyncDialog.vue';
	import { useLinkedLookupSync } from 'src/composables/use-linked-lookup-sync';
	import type { VueComponentElement } from './utils/vue-internals';
	import cognitoMessenger from 'src/utilities/cognito-messaging';
	import type { MenuViewData } from 'src/views/entries/EntryViewsMenu.vue';
	import { globalStore } from 'src/stores/global-store';
	import initEntriesPage from '@legacy/Views/Admin/entries.js';
	import initGridView from './gridview.js';
	import initEntryDetails from '@legacy/Views/Admin/entry-details.js';
	import { resolvePaymentDialogSavedCards } from 'src/views/entries/entry-details/resolve-payment-dialog-saved-cards';
	import session, { getActiveSession } from '@cognitoforms/api/services/session';
	import { useRoute, useRouter } from 'vue-router/composables';
	import { CreateNewEntryEvent, ExportEntriesEvent, BulkActionEvent, PageReadyEvent, RoleAssignmentsUpdated, useEventBus } from './utils/useEventBus';
	import EntriesFormView from 'src/views/entries/EntriesFormView.vue';
	import CEntryViewTabsContainer from 'src/views/entries/EntryViewTabsContainer.vue';
	import CEntryViewActionsContainer from 'src/views/entries/EntryViewActionsContainer.vue';
	import type { PageModel } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/entries-page-controller+-page-model';
	import { cultureDataKey, fieldInfoDataKey, formInfoKey, isMobileKey, navStateKey, organizationInfoKey, tokensKey, localizationResourcesKey } from 'src/utilities/injection-keys';
	import { checkRole } from './utils/role-check';
	import { CancellationToken } from 'src/utilities/cancellation-token';
	import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
	import FormNavigationHeader from 'src/components/navigation/FormNavigationHeader.vue';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import { AppInsights } from 'src/components/embeds/AppInsights';
	import { useFormLightbox } from './utils/use-form-lightbox';
	import { useFormViewLightbox } from './utils/use-form-view-lightbox';
	import { useEntryDetailsLightbox } from 'src/views/entries/entry-details/use-entry-details-lightbox';
	import type { AdminApi } from '../../../../client/src/embed-scripts/admin';
	import type { EntryKey } from '@cognitoforms/types/server-types/forms/services/entry-key';
	import { type FormInfo, type OrganizationInfo, parseFieldInfos } from 'src/components/navigation/context-menu/use-view-settings-dialog-dependencies';
	import { setDocumentTemplateDefaultValues } from 'src/utilities/document-templates';
	import { generateTokens } from './utils/generate-tokens';
	import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
	import type { ClientCultureInfo } from '@cognitoforms/types/server-types/client-culture-info';
	import type { FieldInfo } from '@cognitoforms/types/server-types/model/field-info';
	import { useCurrentForm } from './utils/use-current-form';
	import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
	import type { Token } from 'src/components/form-controls/tokenUtils';
	import { getEntryViews } from 'src/composables/get-entry-views';
	import { getAvatarSVG } from '@cognitoforms/utils/avatar';
	import { expandFieldInfos, convertFieldInfosToPropertyMappings } from 'src/views/admin/utils/field-info-conversion-utils';
	import { addSpaces } from 'src/utilities/stringUtils';

	function getModel(formName: string, viewName: string, entryNumber: string, isTask): Promise<PageModel> {
		return serviceRequest(
			'svc/entries-page/model',
			null,
			{
				method: 'get',
				query: {
					formName: formName,
					viewName: viewName,
					entryNumber: entryNumber,
					isTask: isTask || 'false'
				}
			});
	}

	type PageScriptParamsSource = {
		formUrlName: string;
		entryNumber?: string;
		entryView?: string;
		import?: string;
		bulkDownload?: string;
		formEntryExportId?: string;
		isTask?: boolean;
		cancellationToken?: string;
		orderExportId?: string;
	};

	function injectScript(params: PageScriptParamsSource) {
		return injectDynamicPageScript(
			'entries',
			`${CognitoConfiguration.SiteUrl}svc/entries-page/script` + buildQueryString({
				formName: params.formUrlName,
				viewName: params.entryView,
				entryNumber: params.entryNumber,
				import: params.import,
				formEntryExportId: params.formEntryExportId,
				bulkDownload: params.bulkDownload,
				isTask: params.isTask ? 'true' : 'false',
				cancellationToken: params.cancellationToken || '',
				orderExportId: params.orderExportId
			}));
	}

	function trackFormId(route: Route, formId: string) {
		// Track the form id in history state
		// Also track the route path in order to associate the history state with the route in the future
		history.replaceState({ ...history.state, path: route.path, formId: formId }, null, null);
	}

	function fixFormUrlName(route: Route) {
		// Determine if the current history state is for the route in question (i.e. browser back/forward, as opposed to "normal" navigation)
		if (history.state?.path === route.path) {
			if (history.state.formId && globalStore.forms) {
				const formInfo = globalStore.forms[history.state.formId];
				if (formInfo)
					route.params.formUrlName = formInfo.UrlName;
			}
		}
	}

	function parseEntryViewNumber(entryView: string): number {
		return parseInt(entryView.split('-')[0]);
	}

	function parseViewIdFromRouteParam(formId: string, entryViewParam: string) {
		if (!entryViewParam || Number.isNaN(formId))
			return;

		const entryViewNumber = parseEntryViewNumber(entryViewParam);
		if (isNaN(entryViewNumber))
			return;

		return `${formId}-${entryViewNumber}`;
	}

	function getEntryViewType(formId: string, viewId: string): ViewType {
		if (globalStore && globalStore.currentForm) {
			const view = globalStore.forms[formId].EntryViews.find(v => v.Id === viewId);
			if (view)
				return view.Type;
		}

		const currentView = window.Cognito?.Forms?.model?.currentView;
		if (formId && currentView && currentView.get_Id() === viewId)
			return window.Cognito.Forms.model.currentView.get_Type().get_Name();

		return null;
	}

	function setupLightboxNavigation(
		model: PageModel,
		scriptParams: PageScriptParamsSource,
		to: Route,
		next: NavigationGuardNext
	): PageScriptParamsSource {
		// Return unmodified scriptParams if the user cannot access the requested view
		if (!model.CanAccessRequestedView)
			return scriptParams;

		// Open lightbox on AppHome page if there are no grid views available
		if (model.GuestBackgroundViewId == null) {
			next({
				name: 'home',
				params: { orgcode: model.OrgCode },
				query: {
					formId: model.FormId,
					entryView: to.params.entryView
				}
			});
			return;
		}

		// Prevent route from updating globalStore.currentView and form tree selection
		to.params.lightboxNavigation = 'true';

		// Set the background grid view behind the lightbox
		scriptParams.entryView = model.GuestBackgroundViewParam;
		globalStore.setCurrentView(model.GuestBackgroundViewId);

		return scriptParams;
	}

	export default defineComponent({
		components: {
			EntriesGrid,
			EntriesToolbar,
			EntryDetails,
			EntriesFormView,
			FormNavigationHeader,
			CEntryViewTabsContainer,
			CEntryViewActionsContainer,
			LinkedLookupSyncDialog
		},
		async beforeRouteEnter(to, from, next) {
			try {
				// Prevent navigation errors due to form rename
				fixFormUrlName(to);
				// Handle "admin links", ex: /{orgcode}/{formId}/entries/{entryNumber}
				if (!to.params.entryNumber && to.params.entryView && /^\d+$/.test(to.params.entryView)) {
					to.params.entryNumber = to.params.entryView;
					to.params.entryView = null;
				}
				else if (!to.params.entryView && globalStore?.currentView) {
					// entryView is expected to look like 1-view-name (#- is the only significant part of the value)
					to.params.entryView = globalStore.currentView.Id.split('-')[1] + '-';
				}

				const pageModel = await getModel(to.params.formUrlName, to.params.entryView, to.params.entryNumber, to.params.isTask);
				to.params.model = pageModel as never;
				if (to.params.formUrlName !== pageModel.FormLoweredUrlName)
					to.params.formUrlName = pageModel.FormLoweredUrlName;

				// Prepare script params to set the grid view behind the lightbox
				let scriptParams = structuredClone({ ...to.params, ...to.query } as unknown as PageScriptParamsSource);
				if (pageModel.ClientRole === ClientRole.Guest && pageModel.ViewType === ViewType.Form) {
					scriptParams = setupLightboxNavigation(pageModel, scriptParams, to, next);
					if (!scriptParams)
						return;
				}

				next(async (vm: Vue & LegacyPage & { handleLightboxNavigation: (model: PageModel, to: Route, from: Route) => void, isFormViewReady: boolean }) => {
					try {
						// If the user navigates rapidly to the page more than once, wait for the existing
						// navigation to complete to avoid errors due to concurrent page setup
						if (vm.initialized) {
							await vm.initialized;
							await vm.disposeLegacyPage();
						}
						trackFormId(to, pageModel.FormId);
						vm.initialized = vm.initializeLegacyPage(from.fullPath, scriptParams);
						await vm.initialized;
						vm.trackRecentPages();
						vm.isFormViewReady = true;

						if (pageModel.ClientRole === ClientRole.Guest && pageModel.ViewType === ViewType.Form && pageModel.CanAccessRequestedView)
							vm.handleLightboxNavigation(pageModel, to, from);
					}
					catch (error) {
						handlePageAccessError(error, to, from, next);
					}
				});
			}
			catch (error) {
				handlePageAccessError(error, to, from, next);
			}
		},
		async beforeRouteUpdate(to, from, next) {
			to.params['createNewEntry'] = to.params.createNewEntry || false as unknown as string;

			if (!to.params.entryNumber) {
				this.closeFormLightbox();
				this.closeFormViewLightbox();
				this.closeEntryDetailsLightbox();
			}

			if (to.params.lightboxNavigation) {
				next();
				return;
			}

			// Ensure form view is not shown until Cognito.Forms.model.currentView is initialized
			this.isFormViewReady = false;

			if (this.initialized)
				await this.initialized;

			// Don't reload the page when changing between views, entries, etc.
			// since entries.js manages state changes and does not require a full reload.
			if (from.params.formUrlName === to.params.formUrlName) {
				const viewId = parseViewIdFromRouteParam(this.model.FormId, to.params.entryView);

				// route params can be inconsistent for free orgs, so fall back to Table if we can't resolve the view type
				const viewType = to.params.entryView && this.globalStore
					? getEntryViewType(this.model.FormId, viewId) ?? 'Table'
					: 'Table';

				(to.params as Dictionary<unknown>).model = {
					...this.model,
					ViewType: viewType,
					CanAccessRequestedView: !!globalStore.currentForm?.EntryViews?.find(v => v.Id === viewId)
				};

				if (this.model.ClientRole === ClientRole.Guest && viewType === ViewType.Form) {
					to.params.lightboxNavigation = 'true';
					this.handleLightboxNavigation(this.model, to, from);
					next();
					return;
				}

				next();
				trackFormId(to, this.model.FormId);
				// If we're changing entry views, attempt to add it to recent pages
				if (from.params.entryView !== to.params.entryView) {
					this.trackRecentPages();
					if (globalStore.currentForm) {
						const capturedFormId = window.Cognito?.Forms?.model?.formId;
						nextTick(() => this.changeView(globalStore.currentView.Id, capturedFormId).then(() => {
							this.isFormViewReady = true;
						}));
					}
				}
				// Same form, same entry view.
				else {
					this.isFormViewReady = true;
				}
				setTimeout(() => this.$emit('component-loaded'));
				return;
			}

			// Prevent navigation errors due to form rename
			fixFormUrlName(to);

			try {
				// Remove the 'data-show-entry-detail' class so that the details pane does not remain open
				if (this.$refs.entriesRoot)
					(this.$refs.entriesRoot as HTMLElement).removeAttribute('data-show-entry-detail');

				let thisFormChange: CancellationToken;

				try {
					// Cancel if there is a pending form change
					if (this.pendingFormChange)
						this.pendingFormChange.cancel();

					// Create a new cancellation token for the new form change op
					thisFormChange = this.pendingFormChange = new CancellationToken();

					// Tear down the old form client
					if (window.AdminForm) {
						window.Sys.Observer.setValue(window.Cognito.Forms.model, 'formId', '');
						await window.AdminForm.destroy(this.model.FormId);
					}

					// Exit if another form change was initiating while destroying the form handle
					if (!thisFormChange.isActive) {
						thisFormChange.dispose();
						return;
					}

					// Remove any embedded forms in the DOM
					this.destroyForms();

					// Destroy ExoWeb entity state
					resetEntityPools();

					document.getElementById('grid').classList.add('c-hide-entries-grid');
				}
				catch (e) {
					handlePageDisposalError(e, to, from, next);
					return;
				}

				const pageModel = await getModel(to.params.formUrlName, to.params.entryView, to.params.entryNumber, to.params.isTask);

				// Exit if another form change was initiating while fetching the model
				if (!thisFormChange.isActive) {
					thisFormChange.dispose();
					return;
				}

				to.params.model = pageModel as never;
				if (to.params.formUrlName !== pageModel.FormLoweredUrlName)
					to.params.formUrlName = pageModel.FormLoweredUrlName;

				// Support cancellation of /script request
				to.params.cancellationToken = thisFormChange.token;

				// Prepare script params to set the grid view behind the lightbox
				let scriptParams = structuredClone({ ...to.params, ...to.query } as unknown as PageScriptParamsSource);
				if (pageModel.ClientRole === ClientRole.Guest && pageModel.ViewType === ViewType.Form) {
					scriptParams = setupLightboxNavigation(pageModel, scriptParams, to, next);
					if (!scriptParams)
						return;
				}

				// Dynamic page script depends on libary scripts (ex: ExoWeb) and "required" scripts (ex: Cognito-core)
				injectScript(scriptParams)
					.then(() => {
						thisFormChange.dispose();

						// Clear out the pending form change if this is the one that's currently pending (i.e. most recent)
						if (this.pendingFormChange === thisFormChange) {
							this.pendingFormChange = null;
						}

						// Discard if the operation was cancelled or if a subsequent operation has been started
						if (thisFormChange.isCancelled || this.pendingFormChange)
							return;

						window.Cognito.Forms.controller.changeForm();

						this.isFormViewReady = true;
					});

				if (this.pendingFormChange === thisFormChange) {
					next();
					trackFormId(to, pageModel.FormId);
					this.trackRecentPages();

					if (pageModel.ClientRole === ClientRole.Guest && pageModel.ViewType === ViewType.Form)
						this.handleLightboxNavigation(pageModel, to, from);

					setTimeout(() => this.$emit('component-loaded'));
				}
			}
			catch (error) {
				handlePageAccessError(error, to, from, next);
			}
		},
		async beforeRouteLeave(to, from, next) {
			await this.initialized;

			// Close lightbox if it is open
			this.closeFormLightbox();
			this.closeFormViewLightbox();
			this.closeEntryDetailsLightbox();

			try {
				// Prompt for unusaved changes if needed
				const navigate = window['Cognito']?.navigate || (() => Promise.resolve());
				await navigate();
				if (window.Cognito?.Forms?.gridController)
					window.Cognito.Forms.gridController.reset();
				if (window.Sys?.Observer && window.Cognito?.Forms?.model)
					window.Sys.Observer.setValue(window.Cognito.Forms.model, 'formId', '');
				if (this.pendingFormChange)
					this.pendingFormChange.cancel();
				await this.disposeLegacyPage();
			}
			catch (e) {
				handlePageDisposalError(e, to, from, next);
				return;
			}

			next();
		},
		inheritAttrs: false,
		props: {
			orgcode: { type: String, required: true },
			formUrlName: { type: String, required: true },
			entryView: { type: String, required: false, default: null },
			entryNumber: { type: String, required: false, default: '' },
			import: { type: String, required: false, default: '' },
			formEntryExportId: { type: String, required: false, default: '' },
			bulkDownload: { type: String, required: false, default: '' },
			isTask: { type: Boolean, required: false, default: false },
			model: { type: Object as PropType<PageModel>, required: true },
			createNewEntry: { type: Boolean, default: false },
			isNavigating: { type: Boolean, required: true },
			orderExportId: { type: String, required: false, default: '' }
		},
		emits: ['show-account-menu', 'component-loaded'],
		setup(props, { emit }) {
			const route = useRoute();
			const router = useRouter();
			const isMobile = inject(isMobileKey);
			const resources = inject(localizationResourcesKey);
			const root = ref<HTMLElement>(null);
			const entriesRoot = ref<HTMLElement>(null);
			const entriesToolbar = ref<typeof EntriesToolbar>(null);
			const formModel = ref(props.model);
			const isTableView = computed(() => globalStore.currentView ? globalStore.currentView.Type === 'Table' : formModel.value.ViewType === 'Table');
			const selectedFormName = computed(() => globalStore.currentForm?.Name);
			const selectedFormId = computed(() => globalStore.currentForm?.Id);
			const { syncStatus, clearSyncStatus } = useLinkedLookupSync(selectedFormId);
			const isArchived = computed(() => globalStore.currentForm?.IsArchived);
			const entryViews = computed(() => globalStore.currentForm?.EntryViews);
			const canEditViewTabs = computed(() => globalStore.currentForm?.CanManageEntries && globalStore.organization.Plan.AllowEntryViews);
			const showWorkflowTasks = computed(() => globalStore.organization?.Plan?.AllowWorkflowTasks);
			const adminForm = ref<AdminApi>();
			const workflowRoleName = computed(() => formModel.value.FormData.Roles.find(role => role.Id === globalStore.currentView?.RoleId)?.Name ?? formModel.value.FormData.Roles.find(role => role.IsInternal)?.Name);
			const isPublic = computed(() => formModel.value.FormData.Roles.find(role => role.Id === globalStore.currentView?.RoleId)?.IsPublic ?? formModel.value.FormData.Roles.find(role => role.IsInternal)?.IsPublic);
			const isOrgOwner = computed(() => checkRole(ClientRole.Owner));
			const fieldInfoData = computed<FieldInfo[]>(() => {
				return session.value?.enabledFeatures.ImprovedFieldPickerTokenLoading
					? expandFieldInfos(formModel.value.FormId, parseFieldInfos(formModel.value.ViewSettings.FieldInfosByForm), formModel.value.ViewSettings.MaxTokensLimit)
					: parseFieldInfos(formModel.value.ViewSettings.FieldInfos);
			});
			const columnFieldInfoData = computed<FieldInfo[]>(() => {
				const lookupFieldInfoIds = fieldInfoData.value.filter(fieldInfo => fieldInfo.FieldType === 'Lookup').map(lookup => lookup.Id);
				return fieldInfoData.value.filter(fieldInfo => !fieldInfo.ExpressionBuilderOnly && !lookupFieldInfoIds.some(lookupId => fieldInfo.Id.startsWith(lookupId + '.')));
			});
			const cultureData = computed<ClientCultureInfo>(() => formModel.value.ViewSettings.Culture);
			const organizationInfo = computed<OrganizationInfo>(() => {
				return {
					organizationName: formModel.value.ViewSettings.OrganizationName,
					defaultReplyToEmail: formModel.value.ViewSettings.DefaultReplyToEmail,
					defaultWorkflowLinkButtonText: formModel.value.ViewSettings.DefaultWorkflowLinkButtonText,
					planAllowsWorkflowLinks: formModel.value.ViewSettings.PlanAllowsWorkflowLinks,
					isLimitedWorkflowLinksPro: formModel.value.ViewSettings.IsLimitedWorkflowLinksPro,
					isVerified: formModel.value.ViewSettings.IsVerified,
					improvedFieldPickerTokenLoadingEnabled: formModel.value.ViewSettings.ImprovedFieldPickerTokenLoadingEnabled
				};
			});
			const formInfo = computed<FormInfo>(() => {
				return {
					isPaymentForm: formModel.value.ViewSettings.IsPaymentForm,
					hasLogo: formModel.value.ViewSettings.HasLogo,
					formEntrySharingEnabled: formModel.value.FormData.WorkflowLinksEnabled,
					emailRoleAssignments: formModel.value.FormData.EmailRoleAssignments,
					roles: formModel.value.FormData.Roles,
					workflowLinkText: formModel.value.ViewSettings.WorkflowLinkText,
					adminLinkText: formModel.value.ViewSettings.AdminLinkText,
					saveAndResumeEnabled: formModel.value.ViewSettings.SaveAndResumeEnabled
				};
			});
			const tokens = computed<Token[]>(() => {
				return generateTokens(
					fieldInfoData.value,
					formModel.value.ViewSettings.IsPaymentForm,
					formModel.value.FormData.WorkflowLinksEnabled,
					formModel.value.ViewSettings.PlanAllowsWorkflowLinks,
					formModel.value.ViewSettings.IsLimitedWorkflowLinksPro,
					formModel.value.FormData.Roles,
					formModel.value.ViewSettings.WorkflowLinkText,
					formModel.value.ViewSettings.AdminLinkText
				);
			});

			provide(fieldInfoDataKey, fieldInfoData);
			provide(cultureDataKey, cultureData);
			provide(tokensKey, tokens);
			provide(organizationInfoKey, organizationInfo);
			provide(formInfoKey, formInfo);

			// Entry View Tabs
			const menuEntryViews = computed<MenuViewData[]>(() => {
				if (!entryViews.value)
					return [];

				const isGuest = globalStore.organization.Role === ClientRole.Guest;
				return entryViews.value
					// Do not show form views to guests in the entry page menus
					.filter(v => !(isGuest && v.Type === ViewType.Form))
					.map(v => {
						const roles = formModel.value.FormData.Roles;
						return {
							Id: v.Id,
							Name: v.Name,
							Role: roles.find(r => r.Id === v.RoleId)?.Name ?? roles.find(r => r.IsInternal)?.Name,
							Entries: v.Entries,
							IsTaskView: v.IsTaskView,
							Valid: v.Valid,
							Type: v.Type
						};
					});
			});

			// Entry Details
			const navState = inject(navStateKey);
			const entryDetails = ref<typeof EntryDetails>(null);

			const entryDetailsPanelWidth = navState.entryDetailsWidth;
			const entryDetailsSnapped = navState.entryDetailsSnapped;
			const isEntryDetailsOpen = navState.isEntryDetailsOpen;

			const trackRecentPages = () => {
				if (!globalStore.currentView)
					return;
				const page: RecentPage = {
					Id: selectedFormId.value,
					Type: globalStore.currentView.Type === 'Table' ? PageType.GridView : PageType.FormView,
					ViewId: globalStore.currentView.Id
				};

				globalStore.trackRecentPage(page, router);
			};

			// if a user navigates directly to a page, the call to trackRecentPages will return early since globalStore is not ready
			// once globalStore is ready, track the page
			globalStore.isReady.then(async () => {
				trackRecentPages();
			});

			// Form lightbox
			const { openFormLightbox, closeFormLightbox } = useFormLightbox(adminForm, false);
			const { openFormViewLightbox, closeFormViewLightbox } = useFormViewLightbox(adminForm, false);
			const { openEntryDetailsLightbox, closeEntryDetailsLightbox } = useEntryDetailsLightbox();

			const waitAndOpenFormLightbox = async (
				entryKey: EntryKey,
				formId: string,
				viewToken: string,
				viewId: string,
				roleId: number,
				title: string,
				inlineActionName: string,
				showLocalizedError: boolean,
				closeCallback?: () => void
			) => {
				await nextTick();
				openFormLightbox(entryKey, formId, viewToken, viewId, roleId, title, inlineActionName, showLocalizedError, undefined, closeCallback);
			};

			const waitAndOpenEntryDetailsLightbox = async ({
				entryKey,
				formId,
				viewToken,
				viewId,
				roleId,
				title,
				closeCallback
			}: {
				entryKey: EntryKey;
				formId: string;
				viewToken?: string;
				viewId?: string;
				roleId?: number;
				title?: string;
				closeCallback?: () => void;
			}) => {
				await nextTick();
				openEntryDetailsLightbox({
					entryId: entryKey.EntryId,
					formId,
					viewToken,
					viewId,
					roleId,
					title,
					closeCallback
				});
			};

			const waitForAdminApi = (): Promise<void> => {
				return new Promise<void>(resolve => {
					const check = () => {
						if (adminForm.value)
							nextTick(() => resolve());
						else
							setTimeout(check, 30);
					};
					check();
				});
			};

			const lightboxNavigation = computed(() => route.params.lightboxNavigation);
			const handleLightboxNavigation = async (model: PageModel, to: Route, from: Route) => {
				const viewId = parseViewIdFromRouteParam(model.FormId, to.params.entryView);
				const sameFormNavigation = (from.params.formUrlName === to.params.formUrlName);

				const afterCloseRoute = router.resolve({
					name: 'entries',
					params: {
						...to.params,
						entryView: sameFormNavigation ? from.params.entryView : model.GuestBackgroundViewParam,
						lightboxNavigation: undefined
					},
					query: to.query
				}).route;

				globalStore.isReady.then(async () => {
					const view = globalStore.forms[model.FormId].EntryViews.find(v => v.Id === viewId);

					await waitForAdminApi();
					openFormViewLightbox(view, router, afterCloseRoute);
				});
			};

			const emitGuestFormViewLightbox = () => {
				const view = globalStore.currentForm.EntryViews.find(v => v.Type === ViewType.Form);
				router.push({
					name: 'entries',
					params: {
						...route.params,
						entryView: `${view.Id.split('-')[1]}-${view.InternalName}`,
						lightboxNavigation: undefined
					},
					query: route.query
				});
			};

			const isLimitedAccess = computed(() => !(globalStore.currentForm?.CanReviewEntries ?? formModel.value.CanReviewEntries));
			const isGuest = computed(() => globalStore?.organization?.Role === ClientRole.Guest);
			const canManageEntries = computed(() => globalStore.currentForm?.CanManageEntries ?? formModel.value.CanManageEntries);
			const canManageForm = computed(() => globalStore.currentForm?.CanManageForm ?? formModel.value.CanManageForm);

			const currentViewEntryCount = ref(null);
			watch(() => globalStore.currentForm, () => {
				currentViewEntryCount.value = null;
			});

			const disableUnreadEntryTracking = computed(() => globalStore.organization?.DisableUnreadEntryTracking);

			// Handles special events; such as the form being deleted or losing permission
			const {
				handleDeletedFormRedirect
			} = useCurrentForm();

			// Handle info change events
			let entryViewSubscription = null;
			let readStateSubscription = null;

			onMounted(() => {
				entryViewSubscription = websocketConnection.subscribeToEvents('EntryView', () => {
					if (globalStore.currentView && !globalStore.currentView.IsDeleted)
						cognitoMessenger.trigger('entriesUpdated', { target: window, data: [globalStore.currentForm.Id] });
				});

				readStateSubscription = websocketConnection.subscribeToEvents('FormEntryInternal', (entryIds) => {
					cognitoMessenger.trigger('readStateUpdated', { target: window, data: entryIds });
				});
			});

			onUnmounted(() => {
				entryViewSubscription.unsubscribe();
				readStateSubscription.unsubscribe();
			});

			// since certain form elements are appended to the body, we can't rely solely on this class being applied in more specific contexts
			onMounted(() => {
				document.body.classList.add('app-styled-form');
				onUnmounted(() => document.body.classList.remove('app-styled-form'));
			});

			function destroyForms() {
				// If the form element exists, destroy the component and remove it from the DOM
				let form: HTMLFormElement & VueComponentElement<Vue>;
				while ((form = root.value.querySelector('.cog-form'))) {
					if (form.__vue__)
						form.__vue__.$destroy();
					form.remove();
				}
			}

			const updateEntryView = (viewId: string, viewMeta: UserEntryViewMeta) => globalStore.updateEntryViewLocally(viewId, viewMeta);

			const deleteEntryView = (viewId: string) => globalStore.deleteView(viewId);

			const reorderEntryView = (viewId: string, afterViewId: string) => globalStore.reorderEntryView(viewId, afterViewId);

			const setEntryDetailsIsOpen = (isOpen: boolean) => {
				navState.setEntryDetailsIsOpen(isOpen);
			};

			const isAssignedView = computed(() => {
				const isUserSpecific = globalStore.currentView?.IsUserSpecific;
				const isAssigned = globalStore.currentView?.IsAssignedRole;

				return canManageEntries.value || isAssigned || isLimitedAccess.value || isUserSpecific;
			});

			const isFormView = computed(() => globalStore.currentView?.Type === 'Form');
			const isFormViewReady = ref(false);

			const allowFormEntryCreation = ref(false);
			const overrideEntryCreation = ref(false);
			const onOverrideFormEntryCreation = (val: boolean) => {
				overrideEntryCreation.value = val;
			};

			watch(() => isFormView, () => {
				if (isFormView.value)
					allowFormEntryCreation.value = false;
			});

			watch(() => allowFormEntryCreation, () => {
				overrideEntryCreation.value = false;
			});

			watch(() => props.model, () => {
				formModel.value = props.model;
			});

			watch(() => ({ formId: globalStore.currentForm?.Id, date: globalStore.currentForm?.LastModifiedOn }), async (newVal, oldVal) => {
				// Only call getModel if the form was actually modified since last fetched (eg. not if the only change is entry unread status)
				if (newVal && oldVal && newVal.formId === oldVal.formId && newVal.date !== oldVal.date && globalStore.currentView && !globalStore.currentView.IsDeleted) {
					formModel.value = await getModel(props.formUrlName, props.entryView, props.entryNumber, props.isTask);
				}
			});

			const preventEntryCreation = computed(() => {
				if (isFormView.value && !allowFormEntryCreation.value)
					return true;

				if (!isAssignedView.value)
					return true;

				return globalStore.currentView?.PreventEntryCreation;
			});

			const hasSelectedEntries = ref(false);
			const onUpdateHasSelectedEntries = (val: boolean) => {
				hasSelectedEntries.value = val;
			};

			const showNewEntryButton = computed(() => {
				if (globalStore.organization?.Role !== ClientRole.Guest)
					return !preventEntryCreation.value && (canManageEntries.value || isAssignedView.value || overrideEntryCreation.value);
				else
					return globalStore.currentForm?.EntryViews?.find(v => v.Type === ViewType.Form) !== undefined;
			});

			const showMobileNewEntryButton = computed(() => {
				if (!showNewEntryButton.value || !isMobile.value)
					return false;

				if (globalStore.organization?.Role !== ClientRole.Guest)
					return !isLimitedAccess.value || !hasSelectedEntries.value;
				else
					return globalStore.currentForm?.EntryViews?.find(v => v.Type === ViewType.Form) !== null;
			});

			const newEntryButtonText = computed(() => {
				if (globalStore.organization?.Role === ClientRole.Guest)
					return globalStore.currentForm?.EntryViews?.find(v => v.Type === ViewType.Form)?.Name || 'New Entry';
				else
					return 'New';
			});

			const showLAMobileActionsMenu = computed(() => {
				if (!isMobile.value || !isLimitedAccess.value)
					return false;

				// Cannot use actions on a form view
				if (globalStore.currentView?.Type === ViewType.Form)
					return false;

				return hasSelectedEntries.value;
			});

			const isInReadOnlyMode = computed(() => globalStore.isInReadOnlyMode);

			const calculateColorMatrix = () => {
				const textColor = getComputedStyle(document.documentElement).getPropertyValue('--text-primary') ?? '#000000';
				const matches = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(textColor.trim());
				const rgb = {
					r: parseInt(matches[1], 16) / 255,
					g: parseInt(matches[2], 16) / 255,
					b: parseInt(matches[3], 16) / 255
				};

				return `0 0 0 0 ${rgb.r} 0 0 0 0 ${rgb.g} 0 0 0 0 ${rgb.b} 0 0 0 1 0`;
			};

			const colorMatrix = ref(calculateColorMatrix());

			watch(() => globalStore.orgAppearanceSettings?.Colors, () => {
				colorMatrix.value = calculateColorMatrix();
			});

			// Event bus
			const { on: onCreateNewEntry, cleanup: cleanupCreateNewEntryEvents } = useEventBus(CreateNewEntryEvent);
			const { on: onExportEntries, emit: emitExportEntries } = useEventBus(ExportEntriesEvent);
			const { on: onPerformBulkAction, emit: emitBulkAction } = useEventBus(BulkActionEvent);
			const { on: onRoleAssignmentsUpdated } = useEventBus(RoleAssignmentsUpdated);
			const { emit: emitPageReady } = useEventBus(PageReadyEvent);

			return {
				isMobile,
				isTableView,
				isFormView,
				isFormViewReady,
				selectedFormName,
				selectedFormId,
				isArchived,
				canEditViewTabs,
				showWorkflowTasks,
				globalStore,
				pendingFormChange: ref(null as CancellationToken),
				initialized: ref(null as Promise<unknown>),
				trackRecentPages,
				waitAndOpenFormLightbox,
				handleLightboxNavigation,
				closeFormLightbox,
				closeFormViewLightbox,
				closeEntryDetailsLightbox,
				menuEntryViews,
				adminForm,
				workflowRoleName,
				isPublic,
				isOrgOwner,
				root,
				entriesRoot,
				entryDetails,
				isEntryDetailsOpen,
				entryDetailsSnapped,
				entryDetailsPanelWidth,
				entriesToolbar,
				destroyForms,
				isLimitedAccess,
				isGuest,
				canManageEntries,
				canManageForm,
				currentViewEntryCount,
				showNewEntryButton,
				newEntryButtonText,
				hasSelectedEntries,
				showMobileNewEntryButton,
				showLAMobileActionsMenu,
				isAssignedView,
				disableUnreadEntryTracking,
				formModel,
				lightboxNavigation,
				tokens,
				formInfo,
				colorMatrix,
				currentViewId: computed(() => globalStore?.currentView?.Id),
				changeView(viewId, formId) {
					if (formId && (!window?.Cognito?.Forms?.model?.formId || window?.Cognito?.Forms?.model?.formId !== formId)) {
						console.log('Discarding view change due to form change from ' + formId + ' to ' + window?.Cognito?.Forms?.model?.formId + '.');
						return;
					}
					currentViewEntryCount.value = null;
					onOverrideFormEntryCreation(false);
					return window.Cognito.Forms.onChangeView(viewId);
				},
				newView(type) {
					cognitoMessenger.trigger('newView', { data: type });
				},
				reorderView(viewIdObject) {
					cognitoMessenger.trigger('reorderView', { data: viewIdObject });
				},
				saveView(viewObject) {
					cognitoMessenger.trigger('saveView', { data: viewObject });
				},
				deleteView(viewIdObject) {
					cognitoMessenger.trigger('deleteView', { data: viewIdObject });
				},
				setNoChanges() {
					allowFormEntryCreation.value = false;
					window.Cognito.Forms.setEntryNoChanges();
				},
				formMounted(formViewAdapter) {
					window.Cognito.Forms.onFormViewMounted(formViewAdapter);
				},
				pageLoaded(props) {
					emitPageReady(props);
				},
				beforeSubmit() {
					window.Cognito.Forms.beforeFormViewSubmit();
				},
				formViewSubmitted() {
					allowFormEntryCreation.value = true;
					window.Cognito.Forms.onFormViewSubmitted();
				},
				exportEntries(exportAllFields, selectedEntries) {
					emitExportEntries([exportAllFields, selectedEntries]);
				},
				bulkAction(action) {
					emitBulkAction(action);
				},
				async initializeLegacyPage(fromPath: string, scriptParams: PageScriptParamsSource) {
					await globalStore.isReady;
					ensureCognitoGlobal();
					await getActiveSession();
					const ai = AppInsights();
					const userEmail = computed(() => globalStore.user.Email);
					const userName = computed(() => globalStore.fullName);
					const userFirstName = computed(() => globalStore.user.FirstName);
					const userLastName = computed(() => globalStore.user.LastName);
					await Promise.all<unknown>([
						useAdminComponents(),
						injectAdminFormApi(formModel.value.ApiKey),
						loadRequiredScripts().then(() =>
							Promise.all([
								initGridView({
									canManageEntries,
									appInsights: ai,
									fromPath,
									columnFieldInfoData: columnFieldInfoData,
									emitPageReady,
									disableUnreadEntryTracking,
									onRoleAssignmentsUpdated,
									getEntryViews,
									resources,
									getAvatarSVG,
									userEmail,
									userName
								}),
								props.model.ClientRole !== 'Guest' ? initEntryDetails({ canManageEntries, emitPageReady, isInReadOnlyMode }) : null,
								loadSharedScript('entry-view'),
								loadSharedScript('entry-list'),
								loadSharedScript('shared'),
								loadSharedScript('SelectInput.cognito-select-input'),
								loadLibraryScript('expression-builder'),
								loadLibraryScript('fileupload'),
								loadLibraryScript('expression-validation'),
								loadLibraryScript('cognito-lookup-field'),
								loadLibraryScript('intellisense'),
								loadAdminScript('entry-export'),

								// Dynamic page script depends on library scripts (ex: ExoWeb) and "required" scripts (ex: Cognito-core)
								injectScript(scriptParams ?? props),

								// Loading ExoWeb views depends on shared scripts (i.e. Cognito.ready)
								appendLibraryExoWebTemplates('expression-builder'),
								appendSharedExoWebTemplates('SelectInput.cognito-select-input'),
								(entriesToolbar.value as any)?.$refs.tmpl.ensureLoaded(),
								(entryDetails.value as any)?.$refs.tmpl.ensureLoaded()
							])
						)
					]);

					adminForm.value = window.AdminForm;
					initEntriesPage({
						emitViewUpdated: updateEntryView,
						currentEntryView: computed(() => globalStore.currentView),
						showNewEntry: computed(() => props.createNewEntry),
						onCreateNewEntry,
						onExportEntries,
						onPerformBulkAction,
						currentViewEntryCount,
						isLimitedAccess,
						isGuest,
						canManageEntries,
						disableUnreadEntryTracking,
						formData: computed(() => formModel.value.FormData),
						setEntryDetailsIsOpen,
						isAssignedView,
						onOverrideFormEntryCreation,
						onUpdateHasSelectedEntries,
						isMobile,
						isArchived,
						waitAndOpenFormLightbox,
						waitAndOpenEntryDetailsLightbox,
						resolvePaymentDialogSavedCards,
						emitGuestFormViewLightbox,
						setDocumentTemplateDefaultValues,
						handleDeletedFormRedirect,
						fieldInfoData,
						columnFieldInfoData,
						cultureData,
						organizationInfo,
						formInfo,
						tokens,
						canManageForm,
						lightboxNavigation,
						guestPortalEnabled: computed(() => globalStore.portal.GuestPortalEnabled),
						getEntryViews,
						deleteEntryView,
						reorderEntryView,
						convertFieldInfosToPropertyMappings,
						userEmail,
						userName,
						userFirstName,
						userLastName,
						addSpaces
					});

					emit('component-loaded');
				},
				syncStatus,
				clearSyncStatus,
				async disposeLegacyPage() {
					cleanupCreateNewEntryEvents();
					// Clean up page state
					if (window.AdminForm)
						window.AdminForm.destroy(formModel.value.FormId);

					window.Cognito?.Forms?.removeEventListeners();
					destroyForms();
					removeAdminFormApi();
					disposeLegacyPageState(root.value);
				}
			};
		}
	});
</script>

<style lang="scss">
@import '@legacy/Views/Admin/entries';
@import '@legacy/Content/expression-builder';
@import '@legacy/Content/fileupload';
@import '@legacy/Content/c-timepicker';
@import '@legacy/Content/c-datepicker';
@import '@legacy/Content/c-typeahead';

.entries-page {

	.is-navigating & .c-entries-menu > *,
	.is-navigating & [id='c-entrylist'],
	.is-navigating & .c-panel-resizer,
	.is-navigating & [id='c-entrydetails'] {
		display: none;
	}

	@keyframes button-slide-in {

		0% {
			transform: translateY(100px);
		}

		100% {
			transform: translateY(0);
		}
	}

	.c-new-entry--mobile {
		bottom: $space__4;
		right: $space__4;
		transform: translateY(100px);
		animation: button-slide-in .2s 2s;
		animation-fill-mode: forwards;
	}
}
</style>
